import React from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '../hooks/useFetchAuth';
import { describeLoad } from '../utils/duplicateCheck.js';
import { weekLabel } from '../utils/mappingUsage.js';

// The load itself, above its files, mapping and data: where and when it was loaded, its sheets,
// the weeks of its series around it (what is still missing) and the other loads of its week.

const formatNumber = (value) => (value ?? 0).toLocaleString('fr-FR');
const formatDate = (value) => (value ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '');

function WeekCell({ week }) {
  const status = week.loaded ? 'chargée' : week.missing ? 'manquante' : 'avant le premier chargement';
  const color = week.loaded
    ? 'bg-blue-500 dark:bg-blue-400'
    : week.missing
      ? 'bg-red-300 dark:bg-red-500/60'
      : 'bg-gray-200 dark:bg-gray-600';
  return (
    <span
      title={`${weekLabel(week.week)} : ${status}${week.current ? ' (ce chargement)' : ''}`}
      className={`h-6 flex-1 rounded-sm ${color} ${week.current ? 'ring-2 ring-offset-2 ring-blue-600 ring-offset-white dark:ring-blue-300 dark:ring-offset-gray-800' : ''}`}
    />
  );
}

function MissingSummary({ series }) {
  const { missing, last_loaded: lastLoaded } = series;
  if (missing.length === 0) {
    return <span className="text-green-700 dark:text-green-400">Aucune semaine manquante jusqu'à la semaine dernière</span>;
  }
  const shown = missing.slice(-6).map(weekLabel).join(', ');
  return (
    <span className="text-red-700 dark:text-red-400">
      {missing.length} semaine{missing.length > 1 ? 's' : ''} manquante{missing.length > 1 ? 's' : ''}
      {missing.length > 6 ? `, dont les dernières : ${shown}` : ` : ${shown}`}
      {lastLoaded && <span className="text-gray-500 dark:text-gray-400"> · dernière chargée : {weekLabel(lastLoaded)}</span>}
    </span>
  );
}

function IngestionSummary({ uuid }) {
  const { data, isLoading, error } = useFetch(['history-load', uuid], `/api/history/${uuid}/load`);
  if (isLoading || error || !data) return null;

  const { load, sheets, series, other_files: otherFiles, elsewhere } = data;
  const totalRows = sheets.reduce((sum, sheet) => sum + (sheet.rows || 0), 0);
  const strip = series.strip;

  return (
    <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Ingestion</p>
          <h1 className="text-2xl font-semibold">
            {weekLabel(load.year * 100 + load.week)}
            <span className="font-normal text-gray-500 dark:text-gray-400"> · {load.shop_sign} · {load.client} · {load.category}</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            <span className="font-medium">{load.filename}</span> · chargé le {formatDate(load.loaded_at)}
            {load.loaded_by && <> par {load.loaded_by}</>}
          </p>
          {load.deleted_at && (
            <p className="mt-2 inline-block rounded bg-red-100 px-2 py-1 text-sm text-red-700 dark:bg-red-900/40 dark:text-red-300">
              Supprimé le {formatDate(load.deleted_at)}{load.deleted_by && <> par {load.deleted_by}</>}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-3xl font-semibold">{formatNumber(totalRows)}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            lignes · {sheets.length} feuille{sheets.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Feuilles chargées</h3>
        <div className="flex flex-wrap gap-2">
          {sheets.map((sheet) => {
            const content = (
              <>
                <span className="font-medium">{sheet.sheet}</span>
                <span className="text-gray-500 dark:text-gray-400"> · {formatNumber(sheet.rows)} lignes · {sheet.columns} col.</span>
              </>
            );
            return sheet.current ? (
              <span key={sheet.uuid} className="rounded-md border border-blue-400 bg-blue-50 px-3 py-1.5 text-sm dark:border-blue-500 dark:bg-blue-900/30" title="La feuille de cette page">
                {content}
              </span>
            ) : (
              <Link key={sheet.uuid} to={`/processor/history/${sheet.uuid}`} className="rounded-md border border-gray-200 px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700" title="Voir cette feuille">
                {content}
              </Link>
            );
          })}
        </div>
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Semaines de {load.shop_sign} · {load.client} · {load.category}
          </h3>
          <p className="text-sm"><MissingSummary series={series} /></p>
        </div>
        <div className="flex gap-1">
          {strip.map((week) => <WeekCell key={week.week} week={week} />)}
        </div>
        <div className="mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{weekLabel(strip[0].week)}</span>
          <span className="flex gap-3">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-blue-500 dark:bg-blue-400" />chargée</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-300 dark:bg-red-500/60" />manquante</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm ring-2 ring-blue-600 dark:ring-blue-300" />ce chargement</span>
          </span>
          <span>{weekLabel(strip[strip.length - 1].week)}</span>
        </div>
      </div>

      {(otherFiles.length > 0 || elsewhere.length > 0) && (
        <div className="space-y-2 text-sm">
          {otherFiles.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 dark:text-gray-200">Autres fichiers de cette semaine</h3>
              <ul className="ml-4 list-disc text-gray-600 dark:text-gray-300">
                {otherFiles.map((other) => <li key={other.filename}>{describeLoad(other)}</li>)}
              </ul>
            </div>
          )}
          {elsewhere.length > 0 && (
            <div>
              <h3 className="font-semibold text-amber-700 dark:text-amber-300">Ce fichier est aussi chargé cette semaine sous</h3>
              <ul className="ml-4 list-disc text-gray-600 dark:text-gray-300">
                {elsewhere.map((other) => (
                  <li key={`${other.shop_sign}-${other.client}-${other.category}-${other.filename}`}>
                    {other.shop_sign} · {other.client} · {other.category} : {describeLoad(other)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default IngestionSummary;
